import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import MensagensPanel from './Notificacoes/MensagensPanel';
import CommandCenterHero from './Notificacoes/CommandCenterHero';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Bell,
  BellOff,
  BellRing,
  Search,
  RefreshCw,
  AlertTriangle,
  Clock,
  Send,
  Phone,
  PhoneOff,
  CheckCircle2,
  Car,
  User,
  Users,
  MessageSquare,
  X,
  Info,
  Bookmark,
  BookmarkCheck,
  Zap,
  Settings,
  History,
  WifiOff,
  Wifi,
  QrCode,
  LogOut,
  BarChart3,
  Eye,
  CheckCheck,
  XCircle,
  Smartphone,
  Calendar,
  ChevronDown,
  ChevronRight,
  Plus,
  FileText,
} from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const fmtDate = (s) => {
  if (!s) return '—';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
};

const fmtDateTime = (s) => {
  if (!s) return null;
  try {
    return new Date(s).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return s; }
};

// Remove formatação do telefone para enviar ao WhatsApp
const cleanPhone = (t) => t?.replace(/\D/g, '') || '';

// ─── Templates ──────────────────────────────────────────────────────────────

const TEMPLATES = [
  {
    id: 'vencimento_hoje',
    label: 'Vencimento hoje',
    diasMin: 0,
    diasMax: 0,
    texto: `Olá, {nome}! 👋\nPassamos para lembrá-lo que sua fatura referente ao veículo *{veiculo}* vence *hoje, dia {vencimento}*.\nValor: *{valor}*\nQualquer dúvida, estamos à disposição!\n_Equipe GOLOC_`,
  },
  {
    id: 'atraso_leve',
    label: 'Atraso leve',
    diasMin: 1,
    diasMax: 4,
    texto: `Olá, {nome}! Identificamos que sua fatura do veículo *{veiculo}* está em aberto há *{dias} dia(s)*.\nVencimento: {vencimento}\nValor: *{valor}*\nPor favor, regularize para evitar restrições.\n_Equipe GOLOC_`,
  },
  {
    id: 'aviso_bloqueio',
    label: 'Aviso bloqueio',
    diasMin: 5,
    diasMax: Infinity,
    texto: `⚠️ *Aviso importante — GOLOC*\nOlá, {nome}. Sua fatura do veículo *{veiculo}* encontra-se em atraso há *{dias} dia(s)*.\nValor: *{valor}*\nSeu contrato está sujeito a bloqueio. Entre em contato imediatamente.\n_Equipe GOLOC_`,
  },
];

const getDefaultTemplate = (dias) =>
  TEMPLATES.find((t) => dias >= t.diasMin && dias <= t.diasMax) || TEMPLATES[1];

const applyVars = (text, vars) =>
  text
    .replace(/\{nome\}/g, vars.nome)
    .replace(/\{veiculo\}/g, vars.veiculo)
    .replace(/\{placa\}/g, vars.placa)
    .replace(/\{valor\}/g, vars.valor)
    .replace(/\{vencimento\}/g, vars.vencimento)
    .replace(/\{dias\}/g, vars.dias);

// ─── Atraso Badge ────────────────────────────────────────────────────────────

function AtrasoLabel({ dias }) {
  if (dias === 0) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Hoje</span>;
  if (dias <= 2)  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">{dias}d</span>;
  if (dias <= 6)  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">{dias}d</span>;
  return              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">{dias}d</span>;
}

// ─── Phone Inline Editor ─────────────────────────────────────────────────────

function PhoneCell({ fatura, onPhoneSaved }) {
  const { API } = useAuth();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!value.trim()) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/notificacoes/atualizar-telefone`,
        { client_id: fatura.client_id, telefone: value.trim() },
        { withCredentials: true }
      );
      onPhoneSaved(fatura.fatura_id, value.trim());
      setEditing(false);
      toast.success('Telefone salvo');
    } catch {
      toast.error('Erro ao salvar telefone');
    } finally {
      setSaving(false);
    }
  };

  if (fatura.telefone) {
    return (
      <div className="flex flex-col">
        <span className="text-sm text-gray-700 flex items-center gap-1">
          <Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          {fatura.telefone}
        </span>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <Input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Ex: 5519999991111"
          className="h-7 text-xs w-40"
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          data-testid={`phone-input-${fatura.fatura_id}`}
        />
        <Button size="sm" className="h-7 px-2 text-xs" onClick={save} disabled={saving} data-testid={`phone-save-${fatura.fatura_id}`}>
          {saving ? <div className="animate-spin h-3 w-3 border-b border-white rounded-full" /> : 'OK'}
        </Button>
        <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-medium"
      data-testid={`add-phone-${fatura.fatura_id}`}
    >
      <PhoneOff className="w-3.5 h-3.5" />
      + Telefone
    </button>
  );
}

// ─── Send Modal ───────────────────────────────────────────────────────────────

function EnviarModal({ fatura, onClose, onSent }) {
  const { API } = useAuth();
  const tpl = getDefaultTemplate(fatura.dias_atraso);
  const [selectedTpl, setSelectedTpl] = useState(tpl.id);
  const vars = {
    nome:       fatura.nome_cliente,
    veiculo:    fatura.nome_veiculo,
    placa:      fatura.placa,
    valor:      fmt(fatura.valor_atualizado),
    vencimento: fmtDate(fatura.data_vencimento),
    dias:       String(fatura.dias_atraso),
  };
  const [texto, setTexto] = useState(() => applyVars(tpl.texto, vars));
  // rawTplText guarda o texto do template com variáveis (antes de applyVars)
  const [rawTplText, setRawTplText] = useState(() => tpl.texto);
  const [sending, setSending] = useState(false);

  // ── Templates salvos ────────────────────────────────────────────────────────
  const [savedTpls, setSavedTpls]         = useState([]);
  const [loadingTpls, setLoadingTpls]     = useState(true);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saveNome, setSaveNome]           = useState('');
  const [saving, setSaving]               = useState(false);

  // ── Templates fixos customizados ─────────────────────────────────────────────
  const [fixedTpls, setFixedTpls]     = useState({});
  const [savingFixed, setSavingFixed] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/notificacoes/templates`, { withCredentials: true }),
      axios.get(`${API}/notificacoes/templates/fixos`, { withCredentials: true }),
    ]).then(([savedRes, fixedRes]) => {
      setSavedTpls(savedRes.data);
      setFixedTpls(fixedRes.data);
      // Aplicar texto customizado ao template inicialmente seleccionado
      const customText = fixedRes.data[tpl.id];
      if (customText) {
        setRawTplText(customText);
        setTexto(applyVars(customText, vars));
      }
    }).catch(() => {}).finally(() => setLoadingTpls(false));
  }, [API]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ────────────────────────────────────────────────────────────────
  // Converte texto com valores reais de volta para template com variáveis
  const reverseVars = (text) => {
    let raw = text;
    const entries = Object.entries(vars)
      .filter(([, v]) => v && String(v).length > 1)
      .sort((a, b) => String(b[1]).length - String(a[1]).length);
    for (const [key, val] of entries) {
      raw = raw.split(String(val)).join(`{${key}}`);
    }
    return raw;
  };

  // ── Handlers ────────────────────────────────────────────────────────────────
  const changeTpl = (id) => {
    setSelectedTpl(id);
    const t = TEMPLATES.find((t) => t.id === id);
    if (t) {
      const rawText = fixedTpls[id] || t.texto;
      setRawTplText(rawText);
      setTexto(applyVars(rawText, vars));
    }
  };

  const handleTextoChange = (e) => {
    const newTexto = e.target.value;
    setTexto(newTexto);
    // Actualizar rawTplText via reverse substitution quando template fixo está seleccionado
    if (selectedTpl && TEMPLATES.find((t) => t.id === selectedTpl)) {
      setRawTplText(reverseVars(newTexto));
    }
  };

  const applySavedTpl = (tplSalvo) => {
    setSelectedTpl(null);
    setTexto(tplSalvo.mensagem);
  };

  const deleteSavedTpl = async (tplSalvo) => {
    if (!window.confirm(`Deletar o template "${tplSalvo.nome}"?`)) return;
    try {
      await axios.delete(`${API}/notificacoes/templates/${tplSalvo._id}`, { withCredentials: true });
      setSavedTpls((prev) => prev.filter((t) => t._id !== tplSalvo._id));
      toast.success('Template removido');
    } catch {
      toast.error('Erro ao remover template');
    }
  };

  const salvarTemplate = async () => {
    if (!saveNome.trim()) { toast.error('Informe um nome para o template'); return; }
    // Sempre salva o texto com variáveis (rawTplText) para evitar que o nome/placa do cliente atual
    // vaze para outros clientes quando este template for reutilizado no envio em massa
    const mensagemComVars = rawTplText || reverseVars(texto);
    if (!/\{nome\}/.test(mensagemComVars)) {
      if (!window.confirm('⚠️ Este template não contém a variável {nome}. Se usar este template para outro cliente, ele receberá o nome atual literalmente. Deseja mesmo salvar assim?')) {
        return;
      }
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/notificacoes/templates`,
        { nome: saveNome.trim(), mensagem: mensagemComVars },
        { withCredentials: true }
      );
      setSavedTpls((prev) => [res.data, ...prev]);
      setSaveNome('');
      setShowSaveInput(false);
      toast.success('Template salvo!');
    } catch {
      toast.error('Erro ao salvar template');
    } finally {
      setSaving(false);
    }
  };

  const salvarComoPadrao = async () => {
    setSavingFixed(true);
    try {
      await axios.put(`${API}/notificacoes/templates/fixo/${selectedTpl}`,
        { texto: rawTplText },
        { withCredentials: true }
      );
      setFixedTpls((prev) => ({ ...prev, [selectedTpl]: rawTplText }));
      toast.success('Template padrão atualizado!');
    } catch {
      toast.error('Erro ao salvar template padrão');
    } finally {
      setSavingFixed(false);
    }
  };

  const restaurarPadrao = async () => {
    const originalTpl = TEMPLATES.find((t) => t.id === selectedTpl);
    if (!originalTpl) return;
    setSavingFixed(true);
    try {
      await axios.put(`${API}/notificacoes/templates/fixo/${selectedTpl}`,
        { texto: originalTpl.texto },
        { withCredentials: true }
      );
      setFixedTpls((prev) => ({ ...prev, [selectedTpl]: originalTpl.texto }));
      setRawTplText(originalTpl.texto);
      setTexto(applyVars(originalTpl.texto, vars));
      toast.success('Template restaurado para o padrão original');
    } catch {
      toast.error('Erro ao restaurar template');
    } finally {
      setSavingFixed(false);
    }
  };

  const enviar = async () => {
    setSending(true);
    try {
      const telefone = cleanPhone(fatura.telefone);
      const res = await axios.post(`${API}/notificacoes/enviar-whatsapp`,
        { fatura_id: fatura.fatura_id, telefone, mensagem: texto },
        { withCredentials: true }
      );
      toast.success('Mensagem enviada com sucesso!');
      onSent(fatura.fatura_id, res.data.notificacao_enviada_em);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-green-600" />
            Enviar via WhatsApp
          </DialogTitle>
        </DialogHeader>

        {/* Cabeçalho da fatura */}
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm space-y-1">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-400" />
            <span className="font-semibold text-gray-800">{fatura.nome_cliente}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Car className="w-4 h-4 text-gray-400" />
            <span>{fatura.nome_veiculo}</span>
            {fatura.placa && <span className="font-mono bg-gray-200 px-1.5 py-0.5 rounded text-xs">{fatura.placa}</span>}
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Phone className="w-4 h-4 text-gray-400" />
            <span>{fatura.telefone}</span>
          </div>
          <div className="flex items-center gap-3 pt-1 text-xs text-gray-500">
            <span>Venc: <strong>{fmtDate(fatura.data_vencimento)}</strong></span>
            <AtrasoLabel dias={fatura.dias_atraso} />
            <span>Valor: <strong className="text-gray-800">{fmt(fatura.valor_atualizado)}</strong></span>
          </div>
        </div>

        {/* Seleção de template */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Template</p>

          {/* Pills fixos */}
          <div className="flex gap-2 flex-wrap">
            {TEMPLATES.map((t) => (
              <button
                key={t.id}
                onClick={() => changeTpl(t.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  selectedTpl === t.id
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-green-400'
                }`}
                data-testid={`template-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Pills salvos */}
          {loadingTpls ? (
            <span className="text-xs text-gray-400 flex items-center gap-1.5">
              <span className="animate-spin h-3 w-3 border-b border-gray-400 rounded-full inline-block" />
              Carregando templates salvos...
            </span>
          ) : savedTpls.length > 0 && (
            <div className="flex items-start gap-2 flex-wrap">
              <span className="text-xs text-gray-400 mt-1.5 shrink-0">Meus templates:</span>
              {savedTpls.map((t) => (
                <span
                  key={t._id}
                  className={`inline-flex items-center gap-1 pl-3 pr-1.5 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                    selectedTpl === t._id
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                  }`}
                  data-testid={`saved-template-${t._id}`}
                >
                  <span onClick={() => { setSelectedTpl(t._id); applySavedTpl(t); }}>{t.nome}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSavedTpl(t); }}
                    className={`ml-0.5 rounded-full p-0.5 hover:bg-black/10 transition-colors ${selectedTpl === t._id ? 'text-white/80 hover:text-white' : 'text-gray-400 hover:text-red-500'}`}
                    data-testid={`delete-template-${t._id}`}
                    title={`Remover template "${t.nome}"`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Texto editável */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Mensagem</p>
          <textarea
            className="w-full border border-gray-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-400"
            rows={8}
            value={texto}
            onChange={handleTextoChange}
            data-testid="mensagem-textarea"
          />

          {/* Dica de variáveis */}
          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
            <Info className="w-3 h-3" />
            Variáveis: <code className="bg-gray-100 px-1 rounded">{'{nome}'}</code> ·{' '}
            <code className="bg-gray-100 px-1 rounded">{'{veiculo}'}</code> ·{' '}
            <code className="bg-gray-100 px-1 rounded">{'{placa}'}</code> ·{' '}
            <code className="bg-gray-100 px-1 rounded">{'{valor}'}</code> ·{' '}
            <code className="bg-gray-100 px-1 rounded">{'{vencimento}'}</code> ·{' '}
            <code className="bg-gray-100 px-1 rounded">{'{dias}'}</code>
          </p>

          {/* Salvar como template */}
          {!showSaveInput ? (
            <button
              onClick={() => setShowSaveInput(true)}
              className="mt-2 flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 transition-colors"
              data-testid="btn-salvar-template"
            >
              <Bookmark className="w-3.5 h-3.5" />
              Salvar como template
            </button>
          ) : (
            <div className="mt-2 flex items-center gap-2" data-testid="save-template-form">
              <Input
                autoFocus
                value={saveNome}
                onChange={(e) => setSaveNome(e.target.value)}
                placeholder="Ex: Cobrança gentil"
                className="h-8 text-xs flex-1"
                onKeyDown={(e) => { if (e.key === 'Enter') salvarTemplate(); if (e.key === 'Escape') { setShowSaveInput(false); setSaveNome(''); } }}
                data-testid="input-nome-template"
              />
              <Button
                size="sm"
                className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white gap-1"
                onClick={salvarTemplate}
                disabled={saving}
                data-testid="btn-confirmar-salvar-template"
              >
                {saving ? <div className="animate-spin h-3 w-3 border-b border-white rounded-full" /> : <BookmarkCheck className="w-3.5 h-3.5" />}
                Salvar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-xs text-gray-500"
                onClick={() => { setShowSaveInput(false); setSaveNome(''); }}
                data-testid="btn-cancelar-salvar-template"
              >
                Cancelar
              </Button>
            </div>
          )}

          {/* Salvar como padrão — apenas para templates fixos */}
          {TEMPLATES.find((t) => t.id === selectedTpl) && (
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <button
                onClick={salvarComoPadrao}
                disabled={savingFixed}
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-green-600 transition-colors disabled:opacity-50"
                data-testid="btn-salvar-padrao"
              >
                {savingFixed
                  ? <span className="animate-spin h-3 w-3 border-b border-gray-400 rounded-full inline-block" />
                  : <BookmarkCheck className="w-3.5 h-3.5" />}
                Salvar como padrão
              </button>
              <span className="text-gray-300 text-xs">|</span>
              <button
                onClick={restaurarPadrao}
                disabled={savingFixed}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-orange-500 transition-colors disabled:opacity-50"
                data-testid="btn-restaurar-padrao"
              >
                <RefreshCw className="w-3 h-3" />
                Restaurar padrão
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancelar</Button>
          <Button
            onClick={enviar}
            disabled={sending || !texto.trim()}
            className="bg-green-600 hover:bg-green-700 text-white gap-2"
            data-testid="confirmar-envio"
          >
            {sending ? <div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full" /> : <Send className="w-4 h-4" />}
            Enviar via WhatsApp
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Automação — Regra Card ───────────────────────────────────────────────────

const FAIXA_STYLE = {
  vencimento_hoje: { label: 'Vence hoje',     sub: '0 dias de atraso',  ring: 'border-blue-200   bg-blue-50/60',   dot: 'bg-blue-500'   },
  atraso_leve:     { label: 'Atraso leve',    sub: '1 a 4 dias',        ring: 'border-yellow-200 bg-yellow-50/60', dot: 'bg-yellow-500' },
  atraso_critico:  { label: 'Atraso crítico', sub: '5 ou mais dias',    ring: 'border-red-200    bg-red-50/60',    dot: 'bg-red-500'    },
};

const FIXED_TPLS_OPTIONS = [
  { id: 'vencimento_hoje', nome: 'Vencimento hoje' },
  { id: 'atraso_leve',     nome: 'Atraso leve'     },
  { id: 'aviso_bloqueio',  nome: 'Aviso bloqueio'  },
];

function RegraCard({ regra, savedTpls, onChange }) {
  const style = FAIXA_STYLE[regra.faixa] || { label: regra.faixa, sub: '', ring: 'border-gray-200 bg-gray-50', dot: 'bg-gray-400' };
  const allOptions = [...FIXED_TPLS_OPTIONS, ...savedTpls.map((t) => ({ id: t._id, nome: t.nome }))];

  return (
    <div className={`rounded-xl border p-4 ${style.ring}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* Toggle + label */}
        <div className="flex items-center gap-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={regra.ativo}
              onChange={(e) => onChange({ ...regra, ativo: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600 relative" />
          </label>
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${style.dot}`} />
            <div>
              <p className="font-semibold text-sm text-gray-800">{style.label}</p>
              <p className="text-xs text-gray-500">{style.sub}</p>
            </div>
          </div>
        </div>

        {/* Controlos */}
        <div className="flex items-end gap-4 flex-wrap">
          {/* Faixa de dias (min/max) */}
          <div>
            <p className="text-xs text-gray-500 mb-1">Faixa (dias de atraso)</p>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min="0"
                max="9999"
                value={regra.dias_min ?? 0}
                onChange={(e) => onChange({ ...regra, dias_min: parseInt(e.target.value) || 0 })}
                disabled={!regra.ativo || regra.faixa === 'vencimento_hoje'}
                className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs w-14 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-40 disabled:bg-gray-50"
                data-testid={`regra-diasmin-${regra.faixa}`}
                title="Dias mínimos de atraso para esta faixa"
              />
              <span className="text-xs text-gray-400">→</span>
              <input
                type="number"
                min="0"
                max="9999"
                value={regra.dias_max ?? 9999}
                onChange={(e) => onChange({ ...regra, dias_max: parseInt(e.target.value) || 0 })}
                disabled={!regra.ativo || regra.faixa === 'vencimento_hoje'}
                className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs w-16 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-40 disabled:bg-gray-50"
                data-testid={`regra-diasmax-${regra.faixa}`}
                title="Dias máximos de atraso para esta faixa"
              />
            </div>
          </div>

          {/* Template */}
          <div>
            <p className="text-xs text-gray-500 mb-1">Template da mensagem</p>
            <select
              value={regra.template_id || ''}
              onChange={(e) => onChange({ ...regra, template_id: e.target.value })}
              disabled={!regra.ativo}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-40 bg-white min-w-[140px]"
              data-testid={`regra-template-${regra.faixa}`}
            >
              {allOptions.map((t) => (
                <option key={t.id} value={t.id}>{t.nome}</option>
              ))}
            </select>
          </div>

          {/* Reenviar a cada X dias (não aplicável para "vence hoje") */}
          {regra.faixa !== 'vencimento_hoje' && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Reenviar a cada</p>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={regra.reenviar_a_cada_dias}
                  onChange={(e) => onChange({ ...regra, reenviar_a_cada_dias: parseInt(e.target.value) || 1 })}
                  disabled={!regra.ativo}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs w-16 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-40"
                  data-testid={`regra-reenvio-${regra.faixa}`}
                />
                <span className="text-xs text-gray-500">dias</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Modal QR Code — Gerenciar conexão WhatsApp ───────────────────────────────

function WhatsAppConnectionModal({ open, onClose, API, connectionState, onReload }) {
  const [qrData, setQrData]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const isConnected = connectionState?.status === 'connected';

  const fetchQr = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notificacoes/evolution/qrcode`, { withCredentials: true });
      setQrData(res.data);
      if (res.data?.already_connected) {
        await onReload();
      }
    } catch {
      toast.error('Erro ao obter QR Code');
    } finally {
      setLoading(false);
    }
  }, [API, onReload]);

  // Ao abrir (se desconectado), busca QR
  useEffect(() => {
    if (open && !isConnected) {
      fetchQr();
    } else if (!open) {
      setQrData(null);
    }
  }, [open, isConnected, fetchQr]);

  // Poll a cada 5s enquanto modal estiver aberto e não conectado (para detectar escaneamento + renovar QR)
  useEffect(() => {
    if (!open || isConnected) return;
    const interval = setInterval(async () => {
      await onReload();
      setRefreshTick((t) => t + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [open, isConnected, onReload]);

  // Renovar QR a cada 30s se ainda não conectado
  useEffect(() => {
    if (!open || isConnected || !qrData || qrData.already_connected) return;
    const timer = setTimeout(fetchQr, 30000);
    return () => clearTimeout(timer);
  }, [open, isConnected, qrData, fetchQr, refreshTick]);

  const desconectar = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar a instância WhatsApp? Você precisará escanear o QR Code novamente para reconectar.')) return;
    setLoggingOut(true);
    try {
      await axios.post(`${API}/notificacoes/evolution/logout`, {}, { withCredentials: true });
      toast.success('Instância desconectada. Escaneie o QR para reconectar.');
      await onReload();
      setTimeout(fetchQr, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao desconectar');
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="modal-whatsapp-connection">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-green-600" />
            Conexão WhatsApp
          </DialogTitle>
        </DialogHeader>

        {/* Status atual */}
        {isConnected ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3">
              {connectionState?.profile_pic ? (
                <img src={connectionState.profile_pic} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-green-400" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-green-200 flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-green-700" />
                </div>
              )}
              <div className="flex-1">
                <p className="font-semibold text-green-800 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  Conectado
                </p>
                <p className="text-sm text-gray-700">{connectionState?.profile_name || 'WhatsApp'}</p>
                <p className="text-xs text-gray-500 font-mono">+{connectionState?.numero || '—'}</p>
              </div>
            </div>
            {connectionState?.mudou_numero && (
              <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                ⚠️ O número foi alterado recentemente. Anterior: +{connectionState.numero_anterior}
              </div>
            )}
            <Button
              onClick={desconectar}
              disabled={loggingOut}
              variant="outline"
              className="w-full gap-2 border-red-300 text-red-700 hover:bg-red-50"
              data-testid="btn-logout-whatsapp"
            >
              {loggingOut
                ? <span className="animate-spin h-4 w-4 border-2 border-red-600 border-b-transparent rounded-full inline-block" />
                : <LogOut className="w-4 h-4" />}
              Desconectar e trocar número
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800 leading-relaxed">
              <p className="font-semibold mb-1">Como conectar:</p>
              <ol className="list-decimal ml-4 space-y-0.5 text-xs">
                <li>Abra o WhatsApp no seu celular</li>
                <li>Toque em <strong>Menu</strong> (⋮) → <strong>Aparelhos conectados</strong></li>
                <li>Toque em <strong>Conectar um aparelho</strong></li>
                <li>Aponte a câmera para o QR Code abaixo</li>
              </ol>
            </div>

            <div className="flex flex-col items-center justify-center bg-white border-2 border-dashed border-gray-200 rounded-xl p-6 min-h-[280px]">
              {loading && !qrData ? (
                <span className="animate-spin h-8 w-8 border-4 border-green-600 border-b-transparent rounded-full" />
              ) : qrData?.qr_base64 ? (
                <>
                  <img
                    src={qrData.qr_base64.startsWith('data:') ? qrData.qr_base64 : `data:image/png;base64,${qrData.qr_base64}`}
                    alt="QR Code"
                    className="w-56 h-56"
                    data-testid="img-qrcode"
                  />
                  <p className="text-xs text-gray-400 mt-3">QR renova automaticamente a cada 30s</p>
                  {qrData.pairing_code && (
                    <p className="text-xs text-gray-600 mt-2">
                      Ou use o código: <code className="bg-gray-100 px-2 py-0.5 rounded font-mono font-bold">{qrData.pairing_code}</code>
                    </p>
                  )}
                </>
              ) : (
                <div className="text-center">
                  <QrCode className="w-16 h-16 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Aguardando QR Code...</p>
                  <Button onClick={fetchQr} size="sm" variant="outline" className="mt-3" data-testid="btn-reload-qr">
                    <RefreshCw className="w-3.5 h-3.5 mr-2" />
                    Tentar novamente
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ─── Card de Métricas de Entrega ─────────────────────────────────────────────

// ─── SchedulerStatusCard: painel de diagnóstico do tick automático ───────────
function SchedulerStatusCard({ API, ativo }) {
  const [status, setStatus] = useState(null);
  const [resyncing, setResyncing] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notificacoes/automacao/scheduler-status`, { withCredentials: true });
      setStatus(r.data);
    } catch {/* ignore */}
  }, [API]);

  useEffect(() => {
    carregar();
    const id = setInterval(carregar, 15000);
    return () => clearInterval(id);
  }, [carregar, ativo]);

  const sincronizar = async () => {
    setResyncing(true);
    try {
      await axios.post(`${API}/notificacoes/automacao/sincronizar-scheduler`, {}, { withCredentials: true });
      toast.success('Scheduler ressincronizado');
      carregar();
    } catch {
      toast.error('Erro ao sincronizar');
    } finally {
      setResyncing(false);
    }
  };

  if (!status) return null;
  const tickOk = status.tick_ativo && status.scheduler_running;
  const sincronizado = status.sincronizado;

  return (
    <Card className={tickOk ? 'border-emerald-200' : status.ativo_no_banco ? 'border-red-200' : 'border-slate-200'}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <p className="font-semibold text-sm text-slate-800 flex items-center gap-2">
            <Clock className={`w-4 h-4 ${tickOk ? 'text-emerald-600' : 'text-slate-400'}`} />
            Status do Scheduler
            {tickOk && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-bold uppercase">Tick ativo</span>}
            {!tickOk && status.ativo_no_banco && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded font-bold uppercase">Tick INATIVO!</span>}
            {!status.ativo_no_banco && <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-bold uppercase">Automação OFF</span>}
          </p>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500 tabular-nums">Agora: {status.agora_brt}</span>
            <button onClick={sincronizar} disabled={resyncing}
              className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
              data-testid="btn-resync-scheduler">
              <RefreshCw className={`w-3 h-3 ${resyncing ? 'animate-spin' : ''}`} /> Sincronizar
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-slate-500 mb-0.5 uppercase tracking-wide">Próxima verificação</p>
            <p className="font-semibold text-slate-800 tabular-nums">
              {status.tick_next_run ? new Date(status.tick_next_run).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">a cada 1 minuto</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-slate-500 mb-0.5 uppercase tracking-wide">Horários elegíveis</p>
            <p className="font-semibold text-slate-800 tabular-nums">
              {(status.horarios_elegiveis || []).join(' · ') || '—'}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">global + overrides</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-slate-500 mb-0.5 uppercase tracking-wide">Execuções hoje</p>
            <p className="font-semibold text-slate-800 tabular-nums">{(status.execucoes_hoje || []).length}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">slot/dia (1 vez cada)</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-slate-500 mb-0.5 uppercase tracking-wide">Sincronia</p>
            <p className={`font-semibold tabular-nums ${sincronizado ? 'text-emerald-700' : 'text-red-700'}`}>
              {sincronizado ? '✓ OK' : '⚠ Inconsistente'}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              {sincronizado ? 'config = scheduler' : 'clique em Sincronizar'}
            </p>
          </div>
        </div>

        {(status.execucoes_hoje || []).length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-600 mb-2">Execuções de hoje:</p>
            <div className="space-y-1">
              {status.execucoes_hoje.map((e) => (
                <div key={e.slot_key} className="flex items-center gap-2 text-xs">
                  <span className={`w-1.5 h-1.5 rounded-full ${e.status === 'concluido' ? 'bg-emerald-500' : e.status === 'erro' ? 'bg-red-500' : 'bg-amber-500'}`} />
                  <span className="font-mono tabular-nums">{e.horario}</span>
                  <span className="text-slate-500">·</span>
                  <span className={`${e.status === 'concluido' ? 'text-emerald-700' : e.status === 'erro' ? 'text-red-700' : 'text-amber-700'}`}>
                    {e.status === 'concluido' ? 'concluído' : e.status === 'erro' ? `erro: ${(e.erro || '').slice(0, 60)}` : 'em execução'}
                  </span>
                  {e.iniciado_em && (
                    <span className="text-slate-400 text-[11px] ml-auto">
                      iniciado {new Date(e.iniciado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
          💡 O tick verifica a cada minuto se algum horário é devido. <strong>Não depende</strong> do servidor estar online no minuto exato — se o servidor reiniciar, ele pega no próximo tick (max 1 min de atraso).
        </p>
      </CardContent>
    </Card>
  );
}


// ─── SimulacaoModal: dry-run de "quem seria notificado HOJE" ────────────────
function SimulacaoModal({ API, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/notificacoes/automacao/simular-disparo`, { withCredentials: true });
        if (alive) setData(r.data);
      } catch {
        toast.error('Erro ao simular disparo');
        if (alive) setData({ total_a_enviar: 0, enviados_simulados: [], pulados_motivos: {}, pulados_detalhes: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [API]);

  const motivosLabel = {
    sem_telefone:        'Sem WhatsApp cadastrado',
    ja_enviado_hoje:     'Já foi notificado hoje',
    regra_inativa:       'Regra inativa para esta faixa',
    blacklist_cliente:   'Cliente em blacklist',
    ainda_nao_venceu:    'Ainda não venceu',
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-testid="simulacao-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Eye className="w-5 h-5 text-amber-600" /> Simular disparo de hoje
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Mostra quem seria notificado se a automação rodasse agora — sem enviar nada.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-amber-500 border-b-transparent rounded-full" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">A enviar</p>
                  <p className="text-3xl font-bold text-emerald-900 mt-1" data-testid="sim-total-enviar">{data.total_a_enviar}</p>
                  <p className="text-[11px] text-emerald-700 mt-1">cobrança(s) prevista(s) hoje</p>
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Pulados</p>
                  <p className="text-3xl font-bold text-amber-900 mt-1">
                    {Object.values(data.pulados_motivos || {}).reduce((a, b) => a + b, 0)}
                  </p>
                  <ul className="text-[11px] text-amber-700 mt-1 space-y-0.5">
                    {Object.entries(data.pulados_motivos || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                      <li key={k}><strong>{v}</strong> · {motivosLabel[k] || k}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {data.enviados_simulados?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-2">📋 Quem receberá ({data.enviados_simulados.length})</p>
                  <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold text-slate-700">Cliente</th>
                          <th className="text-left px-3 py-2 font-semibold text-slate-700">Tag</th>
                          <th className="text-left px-3 py-2 font-semibold text-slate-700">Regra</th>
                          <th className="text-right px-3 py-2 font-semibold text-slate-700">Atraso</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {data.enviados_simulados.map((c) => (
                          <tr key={c.client_id} className="hover:bg-slate-50">
                            <td className="px-3 py-2 truncate max-w-[200px]">{c.cliente}</td>
                            <td className="px-3 py-2"><span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded font-medium">#{c.tag}</span></td>
                            <td className="px-3 py-2 text-slate-600">{c.regra_label}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{c.dias_atraso === 0 ? 'hoje' : `+${c.dias_atraso}d`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {data.pulados_detalhes?.length > 0 && (
                <details>
                  <summary className="text-xs font-semibold text-slate-600 cursor-pointer hover:text-slate-900">
                    Ver detalhes dos pulados ({data.pulados_detalhes.length})
                  </summary>
                  <div className="border rounded-lg overflow-hidden mt-2 max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold">Cliente</th>
                          <th className="text-left px-3 py-2 font-semibold">Motivo</th>
                          <th className="text-right px-3 py-2 font-semibold">Atraso</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {data.pulados_detalhes.map((p, i) => (
                          <tr key={i}>
                            <td className="px-3 py-1.5 truncate max-w-[200px]">{p.cliente}</td>
                            <td className="px-3 py-1.5 text-amber-700">{motivosLabel[p.motivo] || p.motivo}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{p.dias_atraso === 0 ? 'hoje' : `+${p.dias_atraso}d`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </>
          )}
        </div>
        <div className="border-t p-4 flex justify-end">
          <Button onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </div>
  );
}



function MetricasCard({ API }) {
  const [metricas, setMetricas] = useState(null);
  const [dias, setDias]         = useState(30);
  const [apenasGoloc, setApenasGoloc] = useState(true);
  const [loading, setLoading]   = useState(true);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notificacoes/metricas?dias=${dias}&apenas_goloc=${apenasGoloc}`, { withCredentials: true });
      setMetricas(res.data);
    } catch {
      setMetricas(null);
    } finally {
      setLoading(false);
    }
  }, [API, dias, apenasGoloc]);

  useEffect(() => { carregar(); }, [carregar]);

  if (loading && !metricas) {
    return (
      <Card>
        <CardContent className="p-5 flex items-center justify-center h-32">
          <span className="animate-spin h-6 w-6 border-4 border-blue-600 border-b-transparent rounded-full" />
        </CardContent>
      </Card>
    );
  }

  const m = metricas || { enviadas: 0, entregues: 0, lidas: 0, falhas: 0, taxa_entrega: 0, taxa_leitura: 0, serie_diaria: [] };
  const maxBar = Math.max(...m.serie_diaria.map((d) => d.enviadas), 1);

  return (
    <Card data-testid="card-metricas">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-indigo-700" />
            </div>
            <div>
              <p className="font-semibold text-sm text-gray-800">Métricas de Entrega</p>
              <p className="text-xs text-gray-500">Status das mensagens enviadas via Evolution API</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer" title="Filtrar só mensagens enviadas pelo GOLOC (exclui as que você manda direto do celular)">
              <input
                type="checkbox"
                checked={apenasGoloc}
                onChange={(e) => setApenasGoloc(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                data-testid="chk-apenas-goloc"
              />
              <span className="text-gray-600">Só GOLOC</span>
            </label>
            <select
              value={dias}
              onChange={(e) => setDias(parseInt(e.target.value))}
              className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white"
              data-testid="select-periodo-metricas"
            >
              <option value="7">Últimos 7 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="60">Últimos 60 dias</option>
              <option value="90">Últimos 90 dias</option>
            </select>
            <Button variant="outline" size="sm" onClick={carregar} disabled={loading} className="gap-1.5" data-testid="btn-refresh-metricas">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Cards numéricos */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3">
            <div className="flex items-center gap-1.5 text-blue-700"><Send className="w-3.5 h-3.5" /><span className="text-[11px] font-medium uppercase tracking-wide">Enviadas</span></div>
            <p className="text-2xl font-bold text-blue-900 mt-1" data-testid="metric-enviadas">{m.enviadas}</p>
          </div>
          <div className="rounded-xl border border-green-200 bg-green-50/40 p-3">
            <div className="flex items-center gap-1.5 text-green-700"><CheckCheck className="w-3.5 h-3.5" /><span className="text-[11px] font-medium uppercase tracking-wide">Entregues</span></div>
            <p className="text-2xl font-bold text-green-900 mt-1" data-testid="metric-entregues">{m.entregues}</p>
            <p className="text-[10px] text-green-700 mt-0.5">{m.taxa_entrega}% de taxa</p>
          </div>
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3">
            <div className="flex items-center gap-1.5 text-indigo-700"><Eye className="w-3.5 h-3.5" /><span className="text-[11px] font-medium uppercase tracking-wide">Lidas</span></div>
            <p className="text-2xl font-bold text-indigo-900 mt-1" data-testid="metric-lidas">{m.lidas}</p>
            <p className="text-[10px] text-indigo-700 mt-0.5">{m.taxa_leitura}% de leitura</p>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50/40 p-3">
            <div className="flex items-center gap-1.5 text-red-700"><XCircle className="w-3.5 h-3.5" /><span className="text-[11px] font-medium uppercase tracking-wide">Falhas</span></div>
            <p className="text-2xl font-bold text-red-900 mt-1" data-testid="metric-falhas">{m.falhas}</p>
          </div>
        </div>

        {/* Gráfico de barras simples (SVG) */}
        {m.serie_diaria.length > 0 && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Envios por dia</p>
            <div className="flex items-end gap-[2px] h-20 border-b border-l border-gray-200 pt-1">
              {m.serie_diaria.map((d, i) => {
                const h = (d.enviadas / maxBar) * 100;
                const hLidas = (d.lidas / maxBar) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col justify-end relative group" title={`${d.dia}: ${d.enviadas} enviadas, ${d.lidas} lidas`}>
                    <div className="bg-blue-300 rounded-t-sm transition-all" style={{ height: `${h}%` }} />
                    <div className="bg-indigo-600 rounded-t-sm absolute bottom-0 left-0 right-0 transition-all" style={{ height: `${hLidas}%` }} />
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-600">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-blue-300 rounded-sm inline-block" /> Enviadas</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-indigo-600 rounded-sm inline-block" /> Lidas</span>
            </div>
          </div>
        )}

        {m.enviadas === 0 && (
          <div className="text-center text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
            <Info className="w-4 h-4 mx-auto mb-1 text-gray-400" />
            Ainda não há mensagens registradas no período. As métricas aparecem aqui quando a Evolution API envia webhooks de status.
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ─── Agenda de Próximos Disparos ──────────────────────────────────────────────

const FAIXA_COLOR = {
  vencimento_hoje: { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700',   dot: 'bg-blue-500',   label: 'Vence hoje' },
  atraso_leve:     { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', dot: 'bg-yellow-500', label: 'Atraso leve' },
  atraso_critico:  { bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-700',    dot: 'bg-red-500',    label: 'Atraso crítico' },
};

function AgendaDisparosCard({ API, automationActive, horaDisparo }) {
  const [agenda, setAgenda]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [dias, setDias]       = useState(7);
  const [expandido, setExpandido] = useState({});  // { '2026-04-22': true }

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notificacoes/automacao/agenda?dias=${dias}`, { withCredentials: true });
      setAgenda(res.data);
    } catch {
      setAgenda(null);
    } finally {
      setLoading(false);
    }
  }, [API, dias]);

  useEffect(() => { carregar(); }, [carregar]);

  const toggleExpandido = (data) => setExpandido((e) => ({ ...e, [data]: !e[data] }));

  const fmtMoeda = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  if (loading && !agenda) {
    return (
      <Card>
        <CardContent className="p-5 flex items-center justify-center h-32">
          <span className="animate-spin h-6 w-6 border-4 border-purple-600 border-b-transparent rounded-full" />
        </CardContent>
      </Card>
    );
  }

  const a = agenda || { total_previstos: 0, por_tag: {}, agenda: [] };
  const totalValor = (a.agenda || []).reduce((s, d) => s + d.disparos.reduce((ss, x) => ss + (x.valor || 0), 0), 0);
  const porTagEntries = Object.entries(a.por_tag || {}).sort((x, y) => y[1] - x[1]);

  return (
    <Card data-testid="card-agenda-disparos">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-purple-700" />
            </div>
            <div>
              <p className="font-semibold text-sm text-gray-800">Agenda de Próximos Disparos</p>
              <p className="text-xs text-gray-500">
                Previsão baseada nas regras actuais
                {automationActive
                  ? <span className="ml-1 text-green-700 font-medium">· Automação ACTIVA às {horaDisparo}</span>
                  : <span className="ml-1 text-gray-500">· <strong>Automação desactivada</strong> (ativa o toggle para disparar)</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={dias}
              onChange={(e) => setDias(parseInt(e.target.value))}
              className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white"
              data-testid="select-horizonte-agenda"
            >
              <option value="3">Próximos 3 dias</option>
              <option value="7">Próximos 7 dias</option>
              <option value="14">Próximos 14 dias</option>
              <option value="30">Próximos 30 dias</option>
            </select>
            <Button variant="outline" size="sm" onClick={carregar} disabled={loading} className="gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Resumo */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-3">
            <p className="text-[11px] text-purple-700 font-medium uppercase tracking-wide">Total previstos</p>
            <p className="text-2xl font-bold text-purple-900 mt-1">{a.total_previstos}</p>
          </div>
          {porTagEntries.slice(0, 3).map(([tag, count]) => (
            <div key={tag} className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3">
              <p className="text-[11px] text-indigo-700 font-medium uppercase tracking-wide truncate">#{tag}</p>
              <p className="text-2xl font-bold text-indigo-900 mt-1">{count}</p>
            </div>
          ))}
          {porTagEntries.length === 0 && (
            <div className="md:col-span-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/40 p-3 flex items-center text-xs text-slate-500">
              Sem disparos previstos no horizonte selecionado.
            </div>
          )}
        </div>

        {totalValor > 0 && (
          <p className="text-xs text-gray-500 text-right">
            Valor total envolvido: <span className="font-semibold text-gray-700">{fmtMoeda(totalValor)}</span>
          </p>
        )}

        {/* Timeline por dia */}
        <div className="space-y-2">
          {(a.agenda || []).map((dia) => {
            const isExp = expandido[dia.data];
            const [y, m, d] = dia.data.split('-');
            return (
              <div key={dia.data} className={`rounded-xl border ${dia.total > 0 ? 'border-gray-200' : 'border-gray-100 bg-gray-50/50'}`}>
                <button
                  onClick={() => dia.total > 0 && toggleExpandido(dia.data)}
                  disabled={dia.total === 0}
                  className={`w-full p-3 flex items-center justify-between gap-3 ${dia.total > 0 ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default opacity-70'}`}
                  data-testid={`agenda-dia-${dia.data}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center shrink-0 ${dia.total > 0 ? 'bg-purple-100' : 'bg-gray-100'}`}>
                      <span className="text-[9px] text-gray-500 font-medium leading-none">{dia.dia_semana}</span>
                      <span className={`text-sm font-bold leading-none ${dia.total > 0 ? 'text-purple-800' : 'text-gray-500'}`}>{d}</span>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-800">{d}/{m}/{y}</p>
                      <p className="text-xs text-gray-500">
                        {dia.total === 0
                          ? 'Nenhum disparo previsto'
                          : `${dia.total} disparo${dia.total > 1 ? 's' : ''} previsto${dia.total > 1 ? 's' : ''}`}
                      </p>
                    </div>
                  </div>
                  {dia.total > 0 && (
                    isExp ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>

                {isExp && dia.total > 0 && (
                  <div className="border-t border-gray-100 p-3 space-y-1.5 bg-gray-50/40">
                    {dia.disparos.map((disp, i) => {
                      const tagColor = (disp.tag === 'PADRÃO') ? 'bg-gray-100 text-gray-700 border-gray-200'
                        : 'bg-indigo-100 text-indigo-700 border-indigo-200';
                      return (
                        <div key={i} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-white border border-slate-200 text-xs">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${tagColor}`}>#{disp.tag}</span>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-800 truncate">{disp.cliente}</p>
                              <p className="text-gray-500 truncate">
                                {disp.veiculo} {disp.placa ? `(${disp.placa})` : ''} · {fmtMoeda(disp.valor)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="font-medium text-[10px] uppercase tracking-wide text-slate-700">
                              {disp.dias_atraso === 0 ? 'Vence' : `+${disp.dias_atraso}d`}
                            </span>
                            <span className="text-[10px] text-gray-500 hidden sm:inline">{disp.regra_label}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {a.total_previstos === 0 && (
          <div className="text-center text-xs text-gray-500 bg-gray-50 rounded-lg p-4">
            <Info className="w-4 h-4 mx-auto mb-1 text-gray-400" />
            Nenhum disparo previsto para os próximos {dias} dias com as regras actuais.
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ─── Automação Panel ──────────────────────────────────────────────────────────

function AutomacaoPanel() {
  const { API } = useAuth();

  const [config, setConfig]               = useState(null);
  const [saving, setSaving]               = useState(false);
  const [savedTpls, setSavedTpls]         = useState([]);
  const [evolutionStatus, setEvolution]   = useState(null);
  const [checkingEvo, setCheckingEvo]     = useState(false);
  const [logs, setLogs]                   = useState([]);
  const [loadingLogs, setLoadingLogs]     = useState(true);
  const [executingNow, setExecutingNow]   = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);
  const [testPhone, setTestPhone]         = useState('');
  const [sendingTest, setSendingTest]     = useState(false);
  const [connectionState, setConnectionState] = useState(null);
  const [connModalOpen, setConnModalOpen] = useState(false);
  const [configuringWebhook, setConfiguringWebhook] = useState(false);
  const [simulacaoOpen, setSimulacaoOpen]   = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/notificacoes/automacao/config`,             { withCredentials: true }),
      axios.get(`${API}/notificacoes/automacao/logs`,               { withCredentials: true }),
      axios.get(`${API}/notificacoes/templates`,                    { withCredentials: true }),
      axios.get(`${API}/notificacoes/automacao/status-evolution`,   { withCredentials: true }),
      axios.get(`${API}/notificacoes/evolution/connection-state`,   { withCredentials: true }),
    ]).then(([cfgRes, logsRes, tplRes, evoRes, connRes]) => {
      setConfig(cfgRes.data);
      setLogs(logsRes.data);
      setSavedTpls(tplRes.data);
      setEvolution(evoRes.data);
      setConnectionState(connRes.data);
    }).catch(() => toast.error('Erro ao carregar configurações'))
      .finally(() => setLoadingLogs(false));
  }, [API]);

  const reloadConnectionState = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notificacoes/evolution/connection-state`, { withCredentials: true });
      setConnectionState(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, [API]);

  const configurarWebhook = async () => {
    setConfiguringWebhook(true);
    try {
      const res = await axios.post(`${API}/notificacoes/evolution/configurar-webhook`, {}, { withCredentials: true });
      toast.success('Webhook configurado — métricas agora serão alimentadas automaticamente 🎯');
      console.log('Webhook:', res.data.webhook_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao configurar webhook');
    } finally {
      setConfiguringWebhook(false);
    }
  };

  const checkEvolution = async () => {
    setCheckingEvo(true);
    try {
      const res = await axios.get(`${API}/notificacoes/automacao/status-evolution`, { withCredentials: true });
      setEvolution(res.data);
    } catch {
      setEvolution({ conectado: false, erro: 'Erro de rede' });
    } finally {
      setCheckingEvo(false);
    }
  };

  const reloadLogs = async () => {
    const res = await axios.get(`${API}/notificacoes/automacao/logs`, { withCredentials: true });
    setLogs(res.data);
  };

  const salvar = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notificacoes/automacao/config`, config, { withCredentials: true });
      toast.success('Configuração guardada!');
    } catch {
      toast.error('Erro ao guardar configuração');
    } finally {
      setSaving(false);
    }
  };

  const toggleAtivo = async (novoAtivo) => {
    setTogglingActive(true);
    const configAnterior = config;
    const novoConfig = { ...config, ativo: novoAtivo };
    setConfig(novoConfig);
    try {
      await axios.put(`${API}/notificacoes/automacao/config`, novoConfig, { withCredentials: true });
      toast.success(novoAtivo ? 'Automação ACTIVADA ✅' : 'Automação desactivada');
    } catch {
      setConfig(configAnterior);
      toast.error('Erro ao alterar estado da automação');
    } finally {
      setTogglingActive(false);
    }
  };

  const enviarTeste = async () => {
    const phoneClean = (testPhone || '').replace(/\D/g, '');
    if (phoneClean.length < 10) {
      toast.error('Informe um número válido com DDD (ex: 5519978171304)');
      return;
    }
    setSendingTest(true);
    try {
      await axios.post(
        `${API}/notificacoes/automacao/enviar-teste`,
        { telefone: phoneClean },
        { withCredentials: true }
      );
      toast.success(`Mensagem de teste enviada para ${phoneClean} 🚀`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao enviar teste');
    } finally {
      setSendingTest(false);
    }
  };

  const executarAgora = async () => {
    setExecutingNow(true);
    try {
      await axios.post(`${API}/notificacoes/automacao/executar-agora`, {}, { withCredentials: true });
      toast.success('Automação iniciada! O histórico será actualizado em breve.');
      setTimeout(async () => { await reloadLogs(); setExecutingNow(false); }, 6000);
    } catch {
      toast.error('Erro ao executar automação');
      setExecutingNow(false);
    }
  };

  const updateRegra = (i, updated) => {
    const regras = [...config.regras];
    regras[i] = updated;
    setConfig((c) => ({ ...c, regras }));
  };

  if (!config) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-b-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-5">

      {/* ── COMMAND CENTER HERO (substitui Conexão + Toggle antigos) ── */}
      <CommandCenterHero
        API={API}
        config={config}
        onToggle={toggleAtivo}
        togglingActive={togglingActive}
        connectionState={connectionState}
        onOpenQR={() => setConnModalOpen(true)}
        onExecuteNow={executarAgora}
        executingNow={executingNow}
      />

      {/* ── Envio de Teste Seguro ── */}
      <Card className="border-2 border-dashed border-blue-300 bg-blue-50/30">
        <CardContent className="p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
              <Send className="w-4 h-4 text-blue-700" />
            </div>
            <div>
              <p className="font-semibold text-sm text-gray-800">Enviar mensagem de teste</p>
              <p className="text-xs text-gray-500 mt-0.5">Valide a integração enviando um WhatsApp para um número de teste (sem afectar faturas reais)</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="tel"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="Ex: 5519978171304 (com DDI + DDD)"
              className="flex-1 min-w-[240px] border border-blue-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
              data-testid="input-telefone-teste"
            />
            <Button
              onClick={enviarTeste}
              disabled={sendingTest || !evolutionStatus?.conectado}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              data-testid="btn-enviar-teste"
            >
              {sendingTest
                ? <span className="animate-spin h-4 w-4 border-2 border-white border-b-transparent rounded-full inline-block" />
                : <Send className="w-4 h-4" />}
              Enviar teste
            </Button>
          </div>
          {!evolutionStatus?.conectado && evolutionStatus !== null && (
            <p className="text-xs text-red-600 mt-2">⚠️ Evolution API desconectada — verifique acima antes de testar</p>
          )}
        </CardContent>
      </Card>

      {/* ── Configuração Principal ── */}
      <Card>
        <CardContent className="p-5 space-y-5">

          {/* Horários + Cooldown */}
          <div className="flex items-start gap-6 flex-wrap">

            {/* Múltiplos horários */}
            <div className="flex-1 min-w-[280px]">
              <p className="text-xs text-gray-500 mb-1.5 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Horários do disparo
                <span className="text-[10px] text-gray-400">(envia a mesma mensagem em cada horário)</span>
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {(config.horarios_disparo || [config.hora_disparo || '09:00']).map((h, i) => (
                  <div key={i} className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1">
                    <input
                      type="time"
                      value={h}
                      onChange={(e) => {
                        const arr = [...(config.horarios_disparo || [config.hora_disparo || '09:00'])];
                        arr[i] = e.target.value;
                        setConfig((c) => ({ ...c, horarios_disparo: arr, hora_disparo: arr[0] }));
                      }}
                      className="bg-transparent text-sm focus:outline-none w-[88px]"
                      data-testid={`input-horario-${i}`}
                    />
                    {(config.horarios_disparo?.length || 1) > 1 && (
                      <button
                        onClick={() => {
                          const arr = (config.horarios_disparo || []).filter((_, idx) => idx !== i);
                          setConfig((c) => ({ ...c, horarios_disparo: arr.length ? arr : ['09:00'], hora_disparo: arr[0] || '09:00' }));
                        }}
                        className="text-blue-700 hover:text-red-600 transition-colors"
                        data-testid={`btn-remover-horario-${i}`}
                        title="Remover este horário"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                {(config.horarios_disparo?.length || 1) < 5 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const arr = [...(config.horarios_disparo || [config.hora_disparo || '09:00'])];
                      // Sugere um horário 6h depois do último
                      const last = arr[arr.length - 1] || '09:00';
                      const [hh, mm] = last.split(':').map(Number);
                      const nh = String((hh + 6) % 24).padStart(2, '0');
                      arr.push(`${nh}:${String(mm).padStart(2, '0')}`);
                      setConfig((c) => ({ ...c, horarios_disparo: arr, hora_disparo: arr[0] }));
                    }}
                    className="gap-1 border-dashed border-blue-300 text-blue-600 h-9"
                    data-testid="btn-adicionar-horario"
                  >
                    <Plus className="w-3.5 h-3.5" /> Adicionar horário
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">Fuso: America/Sao_Paulo · Até 5 horários por dia</p>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Aviso: regras agora vivem nas Tags */}
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-violet-900 mb-1 flex items-center gap-2">
              ✨ As regras agora vivem nas <span className="px-1.5 py-0.5 bg-violet-200 rounded">Tags</span>
            </p>
            <p className="text-xs text-violet-700 leading-relaxed">
              Cada tag tem 5 regras fixas (Vencendo hoje, 1d, 2d, 3d, +3d) com mensagens editáveis.
              Para gerir, vá na aba <strong>Mensagens</strong>.
              Limites: <strong>1 envio por cliente por dia</strong>. Sem cooldown nem reenvio configurável.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ── Botões de acção ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={salvar}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            data-testid="btn-salvar-automacao"
          >
            {saving
              ? <span className="animate-spin h-4 w-4 border-2 border-white border-b-transparent rounded-full inline-block" />
              : <BookmarkCheck className="w-4 h-4" />}
            Guardar Configuração
          </Button>

          <Button
            variant="outline"
            onClick={configurarWebhook}
            disabled={configuringWebhook}
            className="gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="btn-configurar-webhook"
            title="Configura o webhook na Evolution API para alimentar as métricas automaticamente"
          >
            {configuringWebhook
              ? <span className="animate-spin h-4 w-4 border-2 border-indigo-600 border-b-transparent rounded-full inline-block" />
              : <Settings className="w-4 h-4" />}
            Sincronizar Evolution
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setSimulacaoOpen(true)}
            className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="btn-simular-disparo"
          >
            <Eye className="w-4 h-4" /> Simular disparo
          </Button>
          <Button
            variant="outline"
            onClick={executarAgora}
            disabled={executingNow}
            className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
            data-testid="btn-executar-agora"
          >
            {executingNow
              ? <span className="animate-spin h-4 w-4 border-2 border-green-600 border-b-transparent rounded-full inline-block" />
              : <Zap className="w-4 h-4" />}
            Executar Agora
          </Button>
        </div>
      </div>

      {simulacaoOpen && <SimulacaoModal API={API} onClose={() => setSimulacaoOpen(false)} />}

      {/* ── Status do Scheduler (tick + execuções) ── */}
      <SchedulerStatusCard API={API} ativo={config.ativo} />

      {/* ── Métricas de Entrega ── */}
      <MetricasCard API={API} />

      {/* ── Agenda de Próximos Disparos ── */}
      <AgendaDisparosCard API={API} automationActive={config.ativo} horaDisparo={config.hora_disparo} />

      {/* ── Histórico de Execuções ── */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="font-semibold text-sm text-gray-800 flex items-center gap-2">
              <History className="w-4 h-4 text-gray-400" />
              Histórico de Execuções
            </p>
            <button
              onClick={reloadLogs}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
            >
              <RefreshCw className="w-3 h-3" /> Recarregar
            </button>
          </div>

          {loadingLogs ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin h-5 w-5 border-2 border-gray-300 border-b-transparent rounded-full" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <History className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Nenhuma execução registada</p>
              <p className="text-xs mt-1">Clique em "Executar Agora" para testar</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log, i) => {
                const m = log.motivos_pulados || {};
                const totalMotivos = Object.values(m).reduce((s, v) => s + v, 0);
                return (
                  <div key={i} className="p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors space-y-2">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${log.falhos === 0 ? 'bg-green-500' : log.enviados > 0 ? 'bg-yellow-500' : 'bg-red-500'}`} />
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {new Date(log.executado_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          <p className="text-xs text-gray-500">
                            <span className="text-green-700 font-semibold">{log.enviados}</span> enviados ·{' '}
                            <span className="text-amber-700">{log.pulados}</span> pulados ·{' '}
                            <span className="text-red-700">{log.falhos}</span> falhos · {log.duracao_segundos}s
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        log.falhos === 0 && log.enviados > 0 ? 'bg-green-100 text-green-700'
                        : log.falhos === 0 && log.enviados === 0 && log.pulados > 0 ? 'bg-blue-100 text-blue-700'
                        : log.enviados > 0 ? 'bg-yellow-100 text-yellow-700'
                        : log.falhos > 0 ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-600'
                      }`}>
                        {log.falhos === 0 && log.enviados > 0 ? 'Sucesso' :
                         log.falhos === 0 && log.enviados === 0 && log.pulados > 0 ? 'Sem envios' :
                         log.enviados > 0 ? 'Parcial' :
                         log.falhos > 0 ? 'Falhou' : 'Vazio'}
                      </span>
                    </div>

                    {/* Erros detalhados das falhas */}
                    {log.falhos > 0 && log.detalhes?.some(d => d.status === 'falhou') && (
                      <details className="pl-5">
                        <summary className="text-[11px] font-semibold text-red-600 cursor-pointer hover:text-red-800 select-none">
                          Ver erros das {log.falhos} falha{log.falhos !== 1 ? 's' : ''}
                        </summary>
                        <div className="mt-1.5 space-y-1 max-h-40 overflow-y-auto">
                          {log.detalhes.filter(d => d.status === 'falhou').map((d, idx) => (
                            <div key={idx} className="text-[11px] bg-red-50 border border-red-100 rounded px-2 py-1">
                              <span className="font-medium text-red-800">{d.nome || d.invoice_id}</span>
                              {d.regra && <span className="text-red-500 ml-1">({d.regra})</span>}
                              {d.erro && <span className="block text-red-600 mt-0.5 font-mono break-all">{d.erro}</span>}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    {/* Motivos detalhados dos pulados */}
                    {totalMotivos > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap pl-5">
                        {m.sem_telefone > 0 && (
                          <span className="text-[11px] bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full border border-orange-200" title="Cliente sem telefone cadastrado">
                            📵 {m.sem_telefone} sem telefone
                          </span>
                        )}
                        {m.ja_enviado_hoje > 0 && (
                          <span className="text-[11px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full border border-blue-200" title="Já recebeu mensagem hoje">
                            ⏱️ {m.ja_enviado_hoje} já enviado hoje
                          </span>
                        )}
                        {m.regra_inativa > 0 && (
                          <span className="text-[11px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full border border-gray-200" title="Regra de cobrança desativada para esta tag">
                            🔕 {m.regra_inativa} regra OFF
                          </span>
                        )}
                        {m.ainda_nao_venceu > 0 && (
                          <span className="text-[11px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full border border-gray-200" title="Fatura ainda não venceu">
                            ⚪ {m.ainda_nao_venceu} não venceu
                          </span>
                        )}
                        {m.horario_nao_corresponde > 0 && (
                          <span className="text-[11px] bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full border border-purple-200" title="Regra configurada para outro horário">
                            🕐 {m.horario_nao_corresponde} outro horário
                          </span>
                        )}
                        {m.blacklist_cliente > 0 && (
                          <span className="text-[11px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full border border-amber-200" title="Cliente com notificações pausadas">
                            🚫 {m.blacklist_cliente} pausados
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Conexão WhatsApp (QR Code) */}
      <WhatsAppConnectionModal
        open={connModalOpen}
        onClose={() => setConnModalOpen(false)}
        API={API}
        connectionState={connectionState}
        onReload={reloadConnectionState}
      />
    </div>
  );
}


// ─── Envio em Massa Modal ─────────────────────────────────────────────────────

function EnviarMassaModal({ faturas, onClose, onSentBatch }) {
  const { API } = useAuth();

  const [fixedTpls, setFixedTpls]     = useState({});
  const [savedTpls, setSavedTpls]     = useState([]);
  const [loadingTpls, setLoadingTpls] = useState(true);

  const defaultTpl = TEMPLATES[1]; // atraso_leve como padrão
  const [selectedTpl, setSelectedTpl]           = useState(defaultTpl.id);
  const [mensagemTemplate, setMensagemTemplate] = useState(defaultTpl.texto);

  const [step, setStep]       = useState('select'); // 'select' | 'sending' | 'done'
  const [progress, setProgress] = useState({ current: 0, total: faturas.length });
  const [results, setResults]   = useState({ enviados: [], falhos: [] });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/notificacoes/templates`,       { withCredentials: true }),
      axios.get(`${API}/notificacoes/templates/fixos`, { withCredentials: true }),
    ]).then(([savedRes, fixedRes]) => {
      setSavedTpls(savedRes.data);
      setFixedTpls(fixedRes.data);
      const customText = fixedRes.data[defaultTpl.id];
      if (customText) setMensagemTemplate(customText);
    }).catch(() => {}).finally(() => setLoadingTpls(false));
  }, [API]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeTpl = (id) => {
    setSelectedTpl(id);
    const t = TEMPLATES.find((t) => t.id === id);
    if (t) setMensagemTemplate(fixedTpls[id] || t.texto);
  };

  const applySavedTpl = (t) => {
    setSelectedTpl(t._id);
    setMensagemTemplate(t.mensagem);
  };

  const firstFatura = faturas[0];
  const previewVars = firstFatura ? {
    nome: firstFatura.nome_cliente, veiculo: firstFatura.nome_veiculo,
    placa: firstFatura.placa, valor: fmt(firstFatura.valor_atualizado),
    vencimento: fmtDate(firstFatura.data_vencimento), dias: String(firstFatura.dias_atraso),
  } : {};
  const previewText = firstFatura ? applyVars(mensagemTemplate, previewVars) : '';

  const iniciarEnvio = async () => {
    // Aviso crítico: template sem {nome} vai mandar mensagem identica pra todos
    if (!/\{nome\}/.test(mensagemTemplate) && faturas.length > 1) {
      const ok = window.confirm(
        `⚠️ Sua mensagem não tem a variável {nome}.\n\n` +
        `TODOS os ${faturas.length} destinatários receberão exatamente a MESMA mensagem (sem personalização pelo nome).\n\n` +
        `Deseja mesmo continuar?`
      );
      if (!ok) return;
    }
    setStep('sending');
    setProgress({ current: 0, total: faturas.length });
    const enviados = [];
    const falhos   = [];

    for (let i = 0; i < faturas.length; i++) {
      const fatura = faturas[i];
      const vars = {
        nome: fatura.nome_cliente,     veiculo: fatura.nome_veiculo,
        placa: fatura.placa,           valor: fmt(fatura.valor_atualizado),
        vencimento: fmtDate(fatura.data_vencimento), dias: String(fatura.dias_atraso),
      };
      try {
        const res = await axios.post(`${API}/notificacoes/enviar-whatsapp`,
          { fatura_id: fatura.fatura_id, telefone: cleanPhone(fatura.telefone), mensagem: applyVars(mensagemTemplate, vars) },
          { withCredentials: true }
        );
        enviados.push({ fatura, notificacao_enviada_em: res.data.notificacao_enviada_em });
      } catch (err) {
        falhos.push({ fatura, erro: err.response?.data?.detail || 'Falha ao enviar' });
      }
      setProgress({ current: i + 1, total: faturas.length });
      setResults({ enviados: [...enviados], falhos: [...falhos] });
    }

    setStep('done');
    onSentBatch(enviados);
  };

  return (
    <Dialog open onOpenChange={step === 'sending' ? undefined : onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Envio em Massa —{' '}
            <span className="text-blue-600">{faturas.length}</span>{' '}
            destinatário{faturas.length !== 1 ? 's' : ''}
          </DialogTitle>
        </DialogHeader>

        {/* ── STEP: select ── */}
        {step === 'select' && (
          <>
            {/* Template pills */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Template</p>
              <div className="flex gap-2 flex-wrap">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => changeTpl(t.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      selectedTpl === t.id
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-green-400'
                    }`}
                    data-testid={`massa-template-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {loadingTpls ? (
                <span className="text-xs text-gray-400 flex items-center gap-1.5">
                  <span className="animate-spin h-3 w-3 border-b border-gray-400 rounded-full inline-block" />
                  Carregando...
                </span>
              ) : savedTpls.length > 0 && (
                <div className="flex items-start gap-2 flex-wrap">
                  <span className="text-xs text-gray-400 mt-1.5 shrink-0">Meus templates:</span>
                  {savedTpls.map((t) => (
                    <button
                      key={t._id}
                      onClick={() => applySavedTpl(t)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        selectedTpl === t._id
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                      }`}
                      data-testid={`massa-saved-tpl-${t._id}`}
                    >
                      {t.nome}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Textarea — raw template com variáveis */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Mensagem <span className="normal-case font-normal text-gray-400">(variáveis serão substituídas automaticamente)</span>
              </p>
              <textarea
                className={`w-full border rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 ${
                  !/\{nome\}/.test(mensagemTemplate)
                    ? 'border-red-300 focus:ring-red-400 bg-red-50/30'
                    : 'border-gray-200 focus:ring-blue-400'
                }`}
                rows={6}
                value={mensagemTemplate}
                onChange={(e) => { setMensagemTemplate(e.target.value); setSelectedTpl(null); }}
                data-testid="massa-mensagem-textarea"
              />
              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                <Info className="w-3 h-3" />
                {'{nome}'} · {'{veiculo}'} · {'{placa}'} · {'{valor}'} · {'{vencimento}'} · {'{dias}'}
              </p>

              {/* Aviso se faltar {nome} ao enviar em massa */}
              {!/\{nome\}/.test(mensagemTemplate) && faturas.length > 1 && (
                <div className="mt-2 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800 flex items-start gap-2">
                  <Info className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">⚠️ Sua mensagem não tem a variável <code className="bg-red-100 px-1 rounded">{'{nome}'}</code></p>
                    <p className="text-[11px] mt-0.5 text-red-700">Isso significa que <strong>todos os {faturas.length} destinatários receberão a mesma mensagem SEM personalização pelo nome</strong>. Se isso não é o que quer, adicione <code className="bg-red-100 px-1 rounded">{'{nome}'}</code> onde o nome do cliente deve aparecer.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Preview */}
            {firstFatura && mensagemTemplate.trim() && (
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
                <p className="text-xs font-semibold text-blue-700 mb-1.5">
                  Preview — {firstFatura.nome_cliente}
                </p>
                <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{previewText}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button
                onClick={iniciarEnvio}
                disabled={!mensagemTemplate.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                data-testid="btn-iniciar-envio-massa"
              >
                <Send className="w-4 h-4" />
                Enviar para {faturas.length} pessoa{faturas.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </>
        )}

        {/* ── STEP: sending ── */}
        {step === 'sending' && (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3">
              <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-b-transparent rounded-full shrink-0" />
              <div>
                <p className="font-medium text-gray-800">Enviando mensagens...</p>
                <p className="text-sm text-gray-500">
                  {progress.current} de {progress.total} enviado{progress.current !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-300"
                style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
              />
            </div>
            {results.falhos.length > 0 && (
              <div className="space-y-1 mt-1">
                {results.falhos.map((f, i) => (
                  <p key={i} className="text-xs text-red-600 flex items-center gap-1">
                    <X className="w-3 h-3 shrink-0" />
                    {f.fatura.nome_cliente}: {f.erro}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── STEP: done ── */}
        {step === 'done' && (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${results.falhos.length === 0 ? 'bg-green-100' : 'bg-yellow-100'}`}>
                <CheckCircle2 className={`w-5 h-5 ${results.falhos.length === 0 ? 'text-green-600' : 'text-yellow-600'}`} />
              </div>
              <div>
                <p className="font-semibold text-gray-800">Envio concluído!</p>
                <p className="text-sm text-gray-500">
                  {results.enviados.length} enviado{results.enviados.length !== 1 ? 's' : ''}
                  {results.falhos.length > 0 && ` · ${results.falhos.length} falha${results.falhos.length !== 1 ? 's' : ''}`}
                </p>
              </div>
            </div>
            {results.falhos.length > 0 && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-1">
                <p className="text-xs font-semibold text-red-700 mb-1">Falhas:</p>
                {results.falhos.map((f, i) => (
                  <p key={i} className="text-xs text-red-600">{f.fatura.nome_cliente}: {f.erro}</p>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={onClose} data-testid="btn-fechar-massa">Fechar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const FAIXA_OPTS = [
  { label: 'Todos os atrasos', test: () => true },
  { label: 'Vencendo hoje',    test: (d) => d === 0 },
  { label: '1 dia',            test: (d) => d === 1 },
  { label: '2 dias',           test: (d) => d === 2 },
  { label: '3 dias',           test: (d) => d === 3 },
  { label: '4 a 6 dias',       test: (d) => d >= 4 && d <= 6 },
  { label: '+7 dias',          test: (d) => d >= 7 },
];

export default function NotificacoesPage() {
  const { API } = useAuth();
  const [faturas, setFaturas]     = useState([]);
  const [resumo, setResumo]       = useState({ vencendo_hoje: 0, em_atraso: 0, ja_notificados: 0, sem_telefone: 0 });
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch]       = useState('');
  const [filtroNotif, setFiltroNotif] = useState('todos');
  const [filtroFaixa, setFiltroFaixa] = useState('0');

  const [modalFatura, setModalFatura] = useState(null);
  const [selectedIds, setSelectedIds]   = useState(new Set());
  const [showMassModal, setShowMassModal] = useState(false);
  const [aba, setAba] = useState('cobrancas'); // 'cobrancas' | 'automacao'

  // ── Load ────────────────────────────────────────────────────────────────────
  const loadData = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const res = await axios.get(`${API}/notificacoes/faturas-vencidas`, { withCredentials: true });
      setFaturas(res.data.faturas || []);
      setResumo(res.data.resumo || {});
    } catch {
      toast.error('Erro ao carregar notificações');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [API]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Optimistic updates ───────────────────────────────────────────────────────
  const handlePhoneSaved = useCallback((faturaId, telefone) => {
    setFaturas((prev) => prev.map((f) =>
      f.fatura_id === faturaId ? { ...f, telefone } : f
    ));
    setResumo((r) => ({ ...r, sem_telefone: Math.max(0, r.sem_telefone - 1) }));
  }, []);

  const handleSent = useCallback((faturaId, notificacao_enviada_em) => {
    setFaturas((prev) => prev.map((f) =>
      f.fatura_id === faturaId ? { ...f, notificacao_enviada_em } : f
    ));
    setResumo((r) => ({ ...r, ja_notificados: r.ja_notificados + 1 }));
  }, []);

  // Template override por fatura
  const [savedTpls, setSavedTpls] = useState([]);
  useEffect(() => {
    axios.get(`${API}/notificacoes/templates`, { withCredentials: true })
      .then((r) => setSavedTpls(r.data || []))
      .catch(() => {});
  }, [API]);

  const handleTemplateOverride = useCallback(async (faturaId, templateId) => {
    try {
      await axios.patch(
        `${API}/invoices/${faturaId}/template-override`,
        { template_id: templateId || null },
        { withCredentials: true }
      );
      setFaturas((prev) => prev.map((f) =>
        f.fatura_id === faturaId ? { ...f, template_override_id: templateId || null } : f
      ));
      toast.success(templateId ? 'Template fixado para esta fatura' : 'Template override removido');
    } catch {
      toast.error('Erro ao actualizar template');
    }
  }, [API]);

  // ── Seleção em massa ─────────────────────────────────────────────────────────
  const toggleSelect = useCallback((faturaId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(faturaId)) next.delete(faturaId);
      else next.add(faturaId);
      return next;
    });
  }, []);

  const handleMassSent = useCallback((enviados) => {
    const map = new Map(enviados.map((e) => [e.fatura.fatura_id, e.notificacao_enviada_em]));
    setFaturas((prev) => prev.map((f) =>
      map.has(f.fatura_id) ? { ...f, notificacao_enviada_em: map.get(f.fatura_id) } : f
    ));
    setResumo((r) => ({ ...r, ja_notificados: r.ja_notificados + enviados.length }));
    // Do NOT close modal or clear selection here — the modal remains in 'done' step
    // until the user clicks "Fechar" (btn-fechar-massa)
  }, []);

  // ── Filters ──────────────────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const faixaFn = FAIXA_OPTS[parseInt(filtroFaixa, 10)]?.test || (() => true);

  const filtered = useMemo(() => {
    let list = faturas;

    if (filtroNotif === 'nao_notificados')  list = list.filter((f) => !f.notificacao_enviada_em);
    if (filtroNotif === 'notificados_hoje') list = list.filter((f) => f.notificacao_enviada_em?.startsWith(today));

    list = list.filter((f) => faixaFn(f.dias_atraso));

    if (search) {
      const s = search.toLowerCase();
      list = list.filter((f) =>
        f.nome_cliente?.toLowerCase().includes(s) ||
        f.nome_veiculo?.toLowerCase().includes(s) ||
        f.placa?.toLowerCase().includes(s) ||
        f.telefone?.includes(s)
      );
    }

    return list;
  }, [faturas, filtroNotif, filtroFaixa, search, faixaFn, today]);

  // ── Seleção: apenas linhas com telefone são seleccionáveis ───────────────────
  const selectableFiltered = filtered.filter((f) => f.telefone);
  const allSelected  = selectableFiltered.length > 0 && selectableFiltered.every((f) => selectedIds.has(f.fatura_id));
  const someSelected = !allSelected && selectableFiltered.some((f) => selectedIds.has(f.fatura_id));
  const selectedFaturas = faturas.filter((f) => selectedIds.has(f.fatura_id) && f.telefone);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableFiltered.map((f) => f.fatura_id)));
    }
  };

  // ── Stat cards ───────────────────────────────────────────────────────────────
  const cards = [
    { label: 'Vencendo hoje',  value: resumo.vencendo_hoje,  Icon: Clock,         color: 'text-blue-600',   bg: 'bg-blue-50',   testid: 'card-vencendo-hoje' },
    { label: 'Em atraso',      value: resumo.em_atraso,      Icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50', testid: 'card-em-atraso' },
    { label: 'Já notificados', value: resumo.ja_notificados, Icon: BellRing,      color: 'text-green-600',  bg: 'bg-green-50',  testid: 'card-ja-notificados' },
    { label: 'Sem telefone',   value: resumo.sem_telefone,   Icon: BellOff,       color: 'text-gray-500',   bg: 'bg-gray-100',  testid: 'card-sem-telefone' },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5" data-testid="notificacoes-page">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope]">Notificações de Cobrança</h1>
          <p className="text-sm text-gray-500">Faturas vencidas e envio de cobranças via WhatsApp</p>
        </div>
        {aba === 'cobrancas' && (
          <Button variant="outline" size="sm" onClick={() => loadData(true)} disabled={refreshing} data-testid="btn-atualizar" className="gap-2 shrink-0">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        <button
          onClick={() => setAba('cobrancas')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            aba === 'cobrancas'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-cobrancas"
        >
          <Bell className="w-4 h-4" />
          Cobranças
        </button>
        <button
          onClick={() => setAba('automacao')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            aba === 'automacao'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-automacao"
        >
          <Zap className="w-4 h-4" />
          Automação
        </button>
        <button
          onClick={() => setAba('mensagens')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            aba === 'mensagens'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-mensagens"
        >
          <FileText className="w-4 h-4" />
          Mensagens
        </button>
      </div>

      {/* ── ABA AUTOMAÇÃO ── */}
      {aba === 'automacao' && <AutomacaoPanel />}

      {/* ── ABA MENSAGENS ── */}
      {aba === 'mensagens' && <MensagensPanel API={API} />}

      {/* ── ABA COBRANÇAS ── */}
      {aba === 'cobrancas' && (<>

      {/* Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Card key={c.label} data-testid={c.testid}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full ${c.bg} flex items-center justify-center shrink-0`}>
                <c.Icon className={`w-5 h-5 ${c.color}`} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
                <p className="text-xs text-gray-500">{c.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">

            {/* Search */}
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome, veículo, placa..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-notificacoes"
              />
            </div>

            {/* Tabs notif */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm shrink-0">
              {[
                { key: 'todos',            label: 'Todos' },
                { key: 'nao_notificados',  label: 'Não notificados' },
                { key: 'notificados_hoje', label: 'Notificados hoje' },
              ].map((o) => (
                <button
                  key={o.key}
                  onClick={() => setFiltroNotif(o.key)}
                  data-testid={`filtro-${o.key}`}
                  className={`px-3 py-2 transition-colors ${
                    filtroNotif === o.key ? 'bg-blue-600 text-white font-medium' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {/* Faixa atraso */}
            <Select value={filtroFaixa} onValueChange={setFiltroFaixa}>
              <SelectTrigger className="w-[180px] h-9 text-sm shrink-0" data-testid="filtro-faixa">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FAIXA_OPTS.map((o, i) => (
                  <SelectItem key={i} value={String(i)}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

          </div>
        </CardContent>
      </Card>

      {/* Counter */}
      <p className="text-sm text-gray-500 -mt-1 px-1">
        {filtered.length} fatura{filtered.length !== 1 ? 's' : ''} encontrada{filtered.length !== 1 ? 's' : ''}
      </p>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-gray-400">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-400" />
            <p className="font-medium">Nenhuma fatura encontrada</p>
            <p className="text-sm mt-1">Tente ajustar os filtros ou clique em Atualizar.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden" data-testid="tabela-notificacoes">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {/* Checkbox select-all */}
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleSelectAll}
                      disabled={selectableFiltered.length === 0}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer disabled:opacity-40 accent-blue-600"
                      data-testid="checkbox-select-all"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Cliente</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Veículo + Placa</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">ID Veíc.</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Telefone</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Vencimento</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Atraso</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Valor Atualizado</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Status Notif.</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Última Notif.</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((f) => {
                  const jaNotificado  = !!f.notificacao_enviada_em;
                  const notifHoje     = f.notificacao_enviada_em?.startsWith(today);
                  const semTelefone   = !f.telefone;
                  const isCritico     = f.dias_atraso >= 7;

                  return (
                    <tr
                      key={f.fatura_id}
                      className={`hover:bg-gray-50 transition-colors ${isCritico ? 'bg-red-50/20' : ''} ${selectedIds.has(f.fatura_id) ? 'bg-blue-50/40' : ''}`}
                      data-testid={`row-${f.fatura_id}`}
                    >
                      {/* Checkbox de seleção */}
                      <td className="px-3 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(f.fatura_id)}
                          onChange={() => toggleSelect(f.fatura_id)}
                          disabled={semTelefone}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed accent-blue-600"
                          data-testid={`checkbox-${f.fatura_id}`}
                        />
                      </td>

                      {/* Cliente */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1.5 font-medium text-gray-900">
                            <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            {f.nome_cliente || '—'}
                            {f.cliente_nao_notificar && (
                              <span
                                className="inline-flex items-center gap-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border border-amber-200"
                                title="Notificações pausadas para este cliente"
                                data-testid={`badge-blacklist-${f.fatura_id}`}
                              >
                                <BellOff className="w-2.5 h-2.5" />
                                Pausado
                              </span>
                            )}
                          </span>
                          <select
                            value={f.template_override_id || ''}
                            onChange={(e) => handleTemplateOverride(f.fatura_id, e.target.value)}
                            className={`text-[10px] px-1.5 py-0.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-400 self-start max-w-[180px] truncate ${
                              f.template_override_id
                                ? 'bg-indigo-50 border-indigo-200 text-indigo-700 font-medium'
                                : 'bg-gray-50 border-gray-200 text-gray-500'
                            }`}
                            title="Template que será usado na próxima notificação automática (override)"
                            data-testid={`select-template-override-${f.fatura_id}`}
                          >
                            <option value="">📌 Automático (usa regra)</option>
                            <option value="vencimento_hoje">Vencimento hoje</option>
                            <option value="atraso_leve">Atraso leve</option>
                            <option value="aviso_bloqueio">Aviso bloqueio</option>
                            {savedTpls.map((t) => (
                              <option key={t._id} value={t._id}>{t.nome}</option>
                            ))}
                          </select>
                        </div>
                      </td>

                      {/* Veículo + Placa */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="flex items-center gap-1.5 text-gray-700">
                            <Car className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            {f.nome_veiculo || '—'}
                          </span>
                          {f.placa && (
                            <span className="font-mono text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded self-start">{f.placa}</span>
                          )}
                        </div>
                      </td>

                      {/* ID Veículo (uso interno) */}
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-400 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded font-mono">
                          {f.vehicle_id || '—'}
                        </span>
                      </td>

                      {/* Telefone */}
                      <td className="px-4 py-3">
                        <PhoneCell fatura={f} onPhoneSaved={handlePhoneSaved} />
                      </td>

                      {/* Vencimento */}
                      <td className="px-4 py-3 text-center text-gray-600 whitespace-nowrap">
                        {fmtDate(f.data_vencimento)}
                      </td>

                      {/* Atraso */}
                      <td className="px-4 py-3 text-center">
                        <AtrasoLabel dias={f.dias_atraso} />
                      </td>

                      {/* Valor */}
                      <td className="px-4 py-3 text-right">
                        <p className="font-semibold text-gray-900">{fmt(f.valor_atualizado)}</p>
                        {f.multa > 0 && (
                          <p className="text-xs text-red-500 mt-0.5">+{fmt(f.multa)} multa</p>
                        )}
                      </td>

                      {/* Status notificação */}
                      <td className="px-4 py-3 text-center">
                        {jaNotificado ? (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                            notifHoje
                              ? 'bg-green-100 text-green-700 border-green-200'
                              : 'bg-blue-100 text-blue-700 border-blue-200'
                          }`} data-testid={`status-notif-${f.fatura_id}`}>
                            <Bell className="w-3 h-3" />
                            {notifHoje
                              ? `Enviado ${fmtDateTime(f.notificacao_enviada_em)?.slice(11, 16)}`
                              : 'Enviado antes'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-red-100 text-red-700 border-red-200"
                            data-testid={`status-notif-${f.fatura_id}`}>
                            <BellOff className="w-3 h-3" />
                            Não notificado
                          </span>
                        )}
                      </td>

                      {/* Última notificação */}
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {fmtDateTime(f.notificacao_enviada_em) || '—'}
                      </td>

                      {/* Ação */}
                      <td className="px-4 py-3 text-center">
                        {semTelefone ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs border-orange-300 text-orange-600 hover:bg-orange-50 h-8"
                            onClick={() => {
                              // Encontra o PhoneCell para abrir inline
                              document.querySelector(`[data-testid="add-phone-${f.fatura_id}"]`)?.click();
                            }}
                            data-testid={`acao-add-phone-${f.fatura_id}`}
                          >
                            <Phone className="w-3.5 h-3.5 mr-1" />
                            + Telefone
                          </Button>
                        ) : jaNotificado ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs border-gray-300 text-gray-500 hover:bg-gray-50 h-8"
                            onClick={() => setModalFatura(f)}
                            data-testid={`acao-reenviar-${f.fatura_id}`}
                          >
                            <RefreshCw className="w-3.5 h-3.5 mr-1" />
                            Reenviar
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="text-xs bg-blue-600 hover:bg-blue-700 text-white h-8"
                            onClick={() => setModalFatura(f)}
                            data-testid={`acao-enviar-${f.fatura_id}`}
                          >
                            <Send className="w-3.5 h-3.5 mr-1" />
                            Enviar
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      </>) /* fim aba === 'cobrancas' */}

      {/* Modal de envio individual */}
      {modalFatura && (
        <EnviarModal
          fatura={modalFatura}
          onClose={() => setModalFatura(null)}
          onSent={handleSent}
        />
      )}

      {/* Modal de envio em massa */}
      {showMassModal && (
        <EnviarMassaModal
          faturas={selectedFaturas}
          onClose={() => { setShowMassModal(false); setSelectedIds(new Set()); }}
          onSentBatch={handleMassSent}
        />
      )}

      {/* Barra sticky de envio em massa */}
      {selectedIds.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-full shadow-2xl border border-gray-700"
          data-testid="bulk-action-bar"
        >
          <span className="text-sm font-semibold tabular-nums">
            {selectedIds.size} selecionado{selectedIds.size !== 1 ? 's' : ''}
          </span>
          <span className="text-gray-600 select-none">|</span>
          {!allSelected && selectableFiltered.length > 0 && (
            <>
              <button
                onClick={toggleSelectAll}
                className="text-xs text-gray-300 hover:text-white transition-colors whitespace-nowrap"
                data-testid="btn-select-all-bar"
              >
                Selecionar todos ({selectableFiltered.length})
              </button>
              <span className="text-gray-600 select-none">|</span>
            </>
          )}
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-gray-400 hover:text-white transition-colors"
            data-testid="btn-limpar-selecao"
          >
            Limpar
          </button>
          <Button
            size="sm"
            className="bg-green-500 hover:bg-green-400 text-white gap-2 rounded-full font-semibold ml-1"
            onClick={() => setShowMassModal(true)}
            data-testid="btn-enviar-massa"
          >
            <Send className="w-3.5 h-3.5" />
            Enviar em Massa
          </Button>
        </div>
      )}
    </div>
  );
}
